// ModullisteCard — Modulliste mit Online-Unterschrift ("Anwesenheitsliste")
// Teilnehmer bestätigt per Unterschrift, alle Module durchgearbeitet zu haben.

import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ClipboardSignature, Download, Loader2, X, CheckCircle2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SignatureModal = ({ onSubmit, onClose, submitting }) => {
  const canvasRef = useRef(null);
  const [hasDrawn, setHasDrawn] = useState(false);
  const [mode, setMode] = useState('kurs');

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.strokeStyle = '#211812';
    ctx.lineWidth = 2.2;
    ctx.lineCap = 'round';
    let drawing = false;

    const pos = (e) => {
      const r = canvas.getBoundingClientRect();
      const p = e.touches ? e.touches[0] : e;
      return { x: ((p.clientX - r.left) / r.width) * canvas.width, y: ((p.clientY - r.top) / r.height) * canvas.height };
    };
    const start = (e) => { e.preventDefault(); drawing = true; const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
    const move = (e) => { if (!drawing) return; e.preventDefault(); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.stroke(); setHasDrawn(true); };
    const end = () => { drawing = false; };

    canvas.addEventListener('mousedown', start); canvas.addEventListener('mousemove', move);
    window.addEventListener('mouseup', end);
    canvas.addEventListener('touchstart', start, { passive: false });
    canvas.addEventListener('touchmove', move, { passive: false });
    canvas.addEventListener('touchend', end);
    return () => {
      canvas.removeEventListener('mousedown', start); canvas.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', end);
      canvas.removeEventListener('touchstart', start); canvas.removeEventListener('touchmove', move);
      canvas.removeEventListener('touchend', end);
    };
  }, []);

  const clear = () => {
    const c = canvasRef.current;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    setHasDrawn(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="modulliste-sign-modal">
      <div className="bg-cream-50 rounded-2xl p-6 w-full max-w-lg">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display text-xl text-espresso">Online unterschreiben</h3>
          <button onClick={onClose} className="text-sand-600 hover:text-espresso" data-testid="modulliste-sign-close"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-[13px] text-sand-600 mb-3">
          Hiermit bestätige ich, alle Kapitel vollständig bearbeitet bzw. mir die Inhalte selbstständig oder anderweitig angeeignet zu haben.
        </p>
        <div className="mb-3 flex flex-wrap gap-2" data-testid="modulliste-lernart">
          {[['kurs', 'Im Kurs bearbeitet'], ['selbst', 'Selbst gelernt'], ['anderweitig', 'Anderweitig erworben']].map(([v, l]) => (
            <button key={v} onClick={() => setMode(v)}
              className="text-[11px] font-semibold px-3 py-1 rounded-full border transition-colors"
              style={mode === v ? { backgroundColor: '#E07A5F', color: '#FBF7F0', borderColor: '#E07A5F' } : { borderColor: '#E5DCCF', color: '#8A8272' }}
              data-testid={`modulliste-lernart-${v}`}>
              {l}
            </button>
          ))}
        </div>
        <canvas ref={canvasRef} width={560} height={180}
          className="w-full bg-white border-2 border-dashed border-cream-400 rounded-xl touch-none"
          data-testid="modulliste-sign-canvas" />
        <div className="mt-4 flex items-center justify-between">
          <button onClick={clear} className="text-[13px] text-sand-600 underline" data-testid="modulliste-sign-clear">Löschen</button>
          <button
            onClick={() => onSubmit(canvasRef.current.toDataURL('image/png'), mode)}
            disabled={!hasDrawn || submitting}
            className="flex items-center gap-1.5 px-5 py-2.5 rounded-full text-[11px] font-bold uppercase tracking-wider text-cream-50 disabled:opacity-50"
            style={{ backgroundColor: '#E07A5F' }}
            data-testid="modulliste-sign-submit"
          >
            {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ClipboardSignature className="w-3.5 h-3.5" />}
            Unterschrift einreichen
          </button>
        </div>
      </div>
    </div>
  );
};

const ModullisteCard = ({ courseId, getAuthHeader }) => {
  const [status, setStatus] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const loadStatus = () => {
    axios.get(`${API}/user/modulliste/${courseId}/status`, getAuthHeader())
      .then((r) => setStatus(r.data)).catch(() => setStatus(null));
  };
  useEffect(loadStatus, [courseId]);

  const submit = async (dataUrl, mode) => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/user/modulliste/${courseId}/sign`, { signature_data: dataUrl, learning_mode: mode || '' }, getAuthHeader());
      toast.success('Unterschrift gespeichert');
      setShowModal(false);
      loadStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    }
    setSubmitting(false);
  };

  const download = async () => {
    setDownloading(true);
    try {
      const r = await axios.get(`${API}/user/modulliste/${courseId}/pdf`, { ...getAuthHeader(), responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = 'Modulliste.pdf'; a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Download');
    }
    setDownloading(false);
  };

  if (!status) return null;

  return (
    <aside className="mb-8 rounded-xl bg-cream-100 border border-cream-300 hover:border-terra/30 transition-colors" data-testid="modulliste-card">
      <div className="flex flex-wrap items-center gap-4 px-5 sm:px-6 py-4">
        <ClipboardSignature className="w-4 h-4 flex-shrink-0 text-terra" strokeWidth={1.7} />
        <div className="flex-1 min-w-[220px]">
          <p className="font-mono text-[10px] uppercase font-semibold tracking-[0.22em] text-terra">Bearbeitungsnachweis</p>
          <p className="text-[13.5px] text-espresso mt-0.5">
            Modulliste ({status.total_modules} Module) online unterschreiben
            {status.signed && (
              <span className="ml-2 inline-flex items-center gap-1 text-[12px] font-semibold" style={{ color: '#2F7A3D' }}>
                <CheckCircle2 className="w-3.5 h-3.5" /> unterschrieben am {new Date(status.signed_at).toLocaleDateString('de-DE')}
              </span>
            )}
          </p>
          {status.requested && !status.signed && (
            <p className="text-[12.5px] font-semibold mt-0.5" style={{ color: '#E07A5F' }} data-testid="modulliste-requested-hint">
              Deine Akademie bittet dich um deine Unterschrift — dauert keine Minute.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowModal(true)}
            className="px-4 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider border border-terra text-terra hover:bg-terra hover:text-cream-50 transition-colors"
            data-testid="modulliste-open-sign">
            {status.signed ? 'Neu unterschreiben' : 'Jetzt unterschreiben'}
          </button>
          {status.signed && (
            <button onClick={download} disabled={downloading}
              className="flex items-center gap-1.5 px-4 py-2 rounded-full text-[11px] font-bold uppercase tracking-wider text-cream-50 disabled:opacity-60"
              style={{ backgroundColor: '#E07A5F' }}
              data-testid="modulliste-download">
              {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" />}
              PDF
            </button>
          )}
        </div>
      </div>
      {showModal && <SignatureModal onSubmit={submit} onClose={() => setShowModal(false)} submitting={submitting} />}
    </aside>
  );
};

export default ModullisteCard;
